import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';

export default function PremiumHome() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPremiumHome();
      setData(res);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err.message ||
        t('premiumHome.fetchError', 'Failed to load your premium dashboard.')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Loading ──────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ padding: '7rem 1.5rem 5rem' }}>
        <style>{`@keyframes premiumSpin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '40vh', color: 'var(--text-muted)',
        }}>
          <div style={{
            width: 36, height: 36,
            border: '3px solid var(--border)',
            borderTopColor: 'var(--orange)',
            borderRadius: '50%',
            animation: 'premiumSpin 0.8s linear infinite',
          }} />
        </div>
      </div>
    );
  }

  /* ── Error ─────────────────────────────────────────────────────────── */
  if (error) {
    return (
      <div style={{ padding: '7rem 1.5rem 5rem' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', minHeight: '40vh', color: 'var(--text-muted)',
        }}>
          <p>{error}</p>
          <button
            onClick={fetchData}
            style={{
              marginTop: '1rem', padding: '0.6rem 1.5rem',
              background: 'transparent', border: '1px solid var(--orange)',
              borderRadius: '6px', color: 'var(--orange)',
              fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            {t('premiumHome.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  const upcoming = data?.upcoming_booking;
  const artists = data?.favorite_artists || [];
  const memberships = data?.memberships;
  const hasMemberships = Array.isArray(memberships) ? memberships.length > 0 : !!memberships;

  /* ── No memberships ───────────────────────────────────────────────── */
  if (!hasMemberships) {
    return (
      <div style={{ padding: '7rem 1.5rem 5rem', maxWidth: '700px', margin: '0 auto' }}>
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '2.5rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>&#9812;</div>
          <h2 style={{
            fontSize: '1.25rem', fontWeight: 700,
            color: 'var(--text-primary)', marginBottom: '0.75rem',
          }}>
            {t('premiumHome.noMembership', 'No Premium Membership Yet')}
          </h2>
          <p style={{
            color: 'var(--text-muted)', fontSize: '0.9rem',
            marginBottom: '1.5rem', lineHeight: 1.6,
          }}>
            {t('premiumHome.noMembershipDesc', 'Request a VIP membership at your favorite salon to unlock priority booking windows and exclusive perks.')}
          </p>
          <Link
            to="/premium/membership"
            style={{
              display: 'inline-block', padding: '0.8rem 2rem',
              background: 'var(--orange)', color: '#050505',
              fontWeight: 700, fontSize: '0.85rem',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              textDecoration: 'none', borderRadius: '6px',
            }}
          >
            {t('premiumHome.requestMembership', 'Request Membership')}
          </Link>
        </div>
      </div>
    );
  }

  /* ── Main content ─────────────────────────────────────────────────── */
  return (
    <div style={{ padding: '7rem 1.5rem 5rem', maxWidth: '960px', margin: '0 auto' }}>

      {/* VIP Badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
        background: 'rgba(var(--orange-rgb, 212,175,55), 0.15)',
        border: '1px solid var(--border)',
        borderRadius: '999px', padding: '0.35rem 1rem',
        fontSize: '0.75rem', letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--orange)',
        marginBottom: '1rem',
      }}>
        <span>&#9812;</span>
        {t('premiumHome.vipMember', 'VIP Member')}
      </div>

      {/* Welcome heading */}
      <h1 style={{
        fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 700,
        lineHeight: 1.2, margin: '0 0 1.5rem',
        color: 'var(--text-primary)',
      }}>
        {t('premiumHome.welcome', 'Welcome back,')}{' '}
        <span style={{ color: 'var(--orange)' }}>
          {user?.name || t('premiumHome.guest', 'Guest')}
        </span>
      </h1>

      {/* Book Priority Window button */}
      <Link
        to="/premium/book"
        style={{
          display: 'inline-block', padding: '0.9rem 2.5rem',
          background: 'var(--orange)', color: '#050505',
          fontWeight: 700, fontSize: '0.85rem',
          letterSpacing: '0.12em', textTransform: 'uppercase',
          textDecoration: 'none', borderRadius: '6px',
          marginBottom: '2.5rem',
        }}
      >
        {t('premiumHome.bookCta', 'Book Priority Window')}
      </Link>

      {/* Upcoming Window Card */}
      {upcoming && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '1.5rem',
          position: 'relative', overflow: 'hidden',
          marginBottom: '2.5rem',
        }}>
          {/* Gold accent bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(90deg, transparent, var(--orange), transparent)`,
          }} />

          <p style={{
            fontSize: '0.7rem', letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'var(--orange)',
            marginBottom: '1rem',
          }}>
            {t('premiumHome.upcomingWindow', 'Upcoming Window')}
          </p>

          {[
            { label: t('premiumHome.date', 'Date'), value: upcoming.date },
            { label: t('premiumHome.time', 'Time Window'), value: upcoming.time },
            { label: t('premiumHome.artist', 'Artist'), value: upcoming.artist_name },
            { label: t('premiumHome.services', 'Services'), value: Array.isArray(upcoming.services) ? upcoming.services.join(', ') : upcoming.services },
          ].map((row, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between',
              marginBottom: '0.6rem', fontSize: '0.9rem',
            }}>
              <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{row.value}</span>
            </div>
          ))}

          <Link
            to={`/premium/on-my-way/${upcoming.id}`}
            style={{
              display: 'block', width: '100%', marginTop: '1.25rem',
              padding: '0.75rem', background: 'transparent',
              border: '1px solid var(--orange)', borderRadius: '6px',
              color: 'var(--orange)', fontWeight: 700, fontSize: '0.8rem',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              textAlign: 'center', textDecoration: 'none',
              boxSizing: 'border-box',
            }}
          >
            {t('premiumHome.onMyWay', "I'M ON MY WAY")}
          </Link>
        </div>
      )}

      {/* Favorite Artists */}
      {artists.length > 0 && (
        <section>
          <h2 style={{
            fontSize: '0.75rem', letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'var(--orange)',
            marginBottom: '1.25rem',
          }}>
            {t('premiumHome.favoriteArtists', 'Favorite Artists')}
          </h2>

          <div style={{
            display: 'flex', gap: '1rem',
            overflowX: 'auto', paddingBottom: '0.5rem',
          }}>
            {artists.map((artist) => (
              <div
                key={artist._id || artist.id}
                style={{
                  flex: '0 0 200px',
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: '12px', padding: '1.25rem', textAlign: 'center',
                }}
              >
                {artist.avatar ? (
                  <img
                    src={artist.avatar}
                    alt={artist.name}
                    style={{
                      width: 64, height: 64, borderRadius: '50%',
                      border: '2px solid var(--orange)',
                      objectFit: 'cover', marginBottom: '0.75rem',
                      background: 'var(--bg-base)',
                    }}
                  />
                ) : (
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    border: '2px solid var(--orange)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.5rem', color: 'var(--orange)',
                    marginBottom: '0.75rem', background: 'var(--bg-base)',
                    margin: '0 auto 0.75rem',
                  }}>
                    {(artist.name || '?')[0].toUpperCase()}
                  </div>
                )}
                <p style={{
                  fontWeight: 600, fontSize: '0.95rem',
                  marginBottom: '0.2rem', color: 'var(--text-primary)',
                }}>
                  {artist.name}
                </p>
                <p style={{
                  fontSize: '0.8rem', color: 'var(--text-muted)',
                  marginBottom: '0.5rem',
                }}>
                  {artist.role || artist.specialization || ''}
                </p>
                {artist.rating != null && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    fontSize: '0.8rem', color: 'var(--orange)',
                  }}>
                    <span>&#9733;</span>
                    {Number(artist.rating).toFixed(1)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
